/**
 * Mid-tier Catchball — Pass results up Path.
 *
 * Mid → mid: `passBall` only (custody moves; no catchball_proposals row).
 * Mid → Path anchor (the executive tier): form + submit a real catchball
 * proposal from the included session, then passBall so the evaluator can
 * accept / return / reject at `/prioritization/cascade/proposal/:id`.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Loader2 } from 'lucide-react';
import { pathAnchorOrgUnitId } from '../../../lib/catchballPathSpine';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { CatchballUndoAction } from './CatchballUndoAction';
import { withdrawCatchballProposal } from '../../../lib/reversals/reversalsService';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { useCatchballTierTurnStore } from '../../../stores/prioritization/catchballTierTurnStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useToast } from '../../ui/Toast';

export interface InboxPassUpResultsCardProps {
  loopId: string;
  cycleId: string;
  trackId: string | null | undefined;
  workspaceId: string;
  /** Ordered Path org-unit ids (anchor → leaf). */
  pathSpine: readonly string[];
  holderOrgUnitId: string | null | undefined;
  iAmHolder: boolean;
  /** Completed sessions the desk has marked include-in-pass-up. */
  includedSessionIds: string[];
  includedSessionCount: number;
}

export function InboxPassUpResultsCard({
  loopId,
  cycleId,
  trackId,
  workspaceId,
  pathSpine,
  holderOrgUnitId,
  iAmHolder,
  includedSessionIds,
  includedSessionCount,
}: InboxPassUpResultsCardProps) {
  const userId = useUserStore((s) => s.userId) ?? '';
  const userName = useUserStore((s) => s.userName) ?? '';
  const { showToast } = useToast();
  const passBall = useCatchballTierTurnStore((s) => s.passBall);
  const formProposal = useCatchballStore((s) => s.formProposal);
  const submitProposal = useCatchballStore((s) => s.submitProposal);
  const recordProposalSession = useCatchballStore((s) => s.recordProposalSession);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const sessions = useSessionStore((s) => s.sessions);
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);

  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittedProposalId, setSubmittedProposalId] = useState<string | null>(null);

  const stopLabel = (orgUnitId: string) => {
    const unit = orgUnits.get(orgUnitId);
    return unit?.displayName?.trim() || unit?.name?.trim() || orgUnitId;
  };

  const stopsAbove = useMemo(() => {
    if (!holderOrgUnitId || pathSpine.length === 0) return [];
    const idx = pathSpine.findIndex((id) => id === holderOrgUnitId);
    if (idx <= 0) return [];
    return pathSpine.slice(0, idx);
  }, [pathSpine, holderOrgUnitId]);

  const defaultTargetId = stopsAbove[stopsAbove.length - 1] ?? null;
  const [targetOrgUnitId, setTargetOrgUnitId] = useState<string | null>(null);
  const effectiveTargetId = targetOrgUnitId ?? defaultTargetId;
  const targetName = effectiveTargetId ? stopLabel(effectiveTargetId) : null;

  const pathAnchorId = pathAnchorOrgUnitId(pathSpine);
  const isFinalToAnchor =
    !!effectiveTargetId && !!pathAnchorId && effectiveTargetId === pathAnchorId;

  const packagingSessionId = useMemo(() => {
    const withRanking = includedSessionIds.find((id) => {
      const s = sessions.find((x) => x.id === id);
      const ranked = s?.rankingOutput?.rankedItemIds?.length ?? 0;
      return ranked > 0 || s?.state === 'RUN_COMPLETE' || s?.state === 'PUBLISHED';
    });
    return withRanking ?? includedSessionIds[0] ?? null;
  }, [includedSessionIds, sessions]);

  if (!iAmHolder || stopsAbove.length === 0) return null;

  const canPass =
    !!userId &&
    !!holderOrgUnitId &&
    !!effectiveTargetId &&
    includedSessionCount > 0 &&
    (!isFinalToAnchor || (!!packagingSessionId && !!trackId && note.trim().length > 0));

  const handlePass = async () => {
    if (!userId || !holderOrgUnitId || !effectiveTargetId || !canPass) return;
    setBusy(true);
    try {
      let proposalId: string | null = null;

      if (isFinalToAnchor) {
        if (!trackId || !packagingSessionId) {
          showToast(
            'error',
            'Cannot package proposal',
            `Include a completed ranking session before sending to ${targetName ?? 'the next level'}.`,
          );
          return;
        }
        const formed = await formProposal({
          sessionId: packagingSessionId,
          trackId,
          loopId,
          cycleId,
          workspaceId,
          actorId: userId,
          actorName: userName || 'Facilitator',
        });
        if (!formed?.proposalId) {
          showToast(
            'error',
            'Could not form proposal',
            useCatchballStore.getState().error ??
              'Session results must be saved with real item IDs. Re-close the ranking session, then try again.',
          );
          return;
        }
        const submitted = await submitProposal({
          proposalId: formed.proposalId,
          rationale: note.trim(),
          actorId: userId,
          workspaceId,
        });
        if (!submitted) {
          showToast(
            'error',
            'Submit failed',
            'Proposal was formed but could not be submitted. Try again.',
          );
          return;
        }
        proposalId = formed.proposalId;

        // Record which sitting backed this proposal and drop it from the loop's
        // pending-send set (mig 670). Also covers the resume path, where
        // formProposal returns an existing draft it did not insert. Best-effort
        // by design: the proposal is already submitted and must not be undone,
        // but a failure here means the desk would keep re-offering a sent
        // session, so it is reported rather than swallowed.
        const recorded = await recordProposalSession({
          proposalId,
          sessionId: packagingSessionId,
          actorId: userId,
        });
        if (!recorded) {
          showToast(
            'error',
            'Proposal sent, but not recorded against the session',
            'It may still show as ready to include. Refresh the desk; if it persists, re-attach the session.',
          );
        }

        setSubmittedProposalId(proposalId);
      }

      const result = await passBall({
        loopId,
        fromOrgUnitId: holderOrgUnitId,
        toOrgUnitId: effectiveTargetId,
        passDirection: 'up',
        content: note.trim() ? { description: note.trim() } : undefined,
        actorId: userId,
        cycleId,
      });
      if (!result.success) {
        showToast(
          'error',
          proposalId ? 'Proposal sent; pass incomplete' : 'Could not pass up',
          proposalId
            ? 'The proposal was submitted — refresh Catchball or open it from notifications.'
            : result.error ?? 'Check that you still hold this ball, then try again.',
        );
        return;
      }

      await fetchLoops(cycleId);
      showToast(
        'success',
        isFinalToAnchor ? `Proposal sent to ${targetName ?? 'the next level'}` : 'Results passed up',
        isFinalToAnchor
          ? `${targetName ?? 'The next level'} can accept, return, or reject.`
          : `${targetName ?? 'Next stop'} can take the ball.`,
      );
      if (!proposalId) setNote('');
    } finally {
      setBusy(false);
    }
  };

  if (submittedProposalId) {
    return (
      <section
        id="inbox-pass-up"
        className="border border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/30 p-4 space-y-2 scroll-mt-24"
      >
        <h2 className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
          Proposal submitted to {targetName ?? 'the next level'}
        </h2>
        <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          Ranking and rationale are packaged for review. {targetName ?? 'The next level'} can accept,
          return with guidance, or reject.
        </p>
        <div className="flex items-center gap-3">
          <Link
            to={`/prioritization/cascade/proposal/${submittedProposalId}`}
            className="inline-block text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] underline hover:no-underline"
          >
            View proposal →
          </Link>
          <CatchballUndoAction
            triggerLabel="Withdraw proposal"
            modalTitle="Withdraw this proposal"
            description={`Nobody at ${targetName ?? 'the next level'} has decided on it yet — withdrawing returns it to you before it was submitted.`}
            confirmLabel="Withdraw proposal"
            reasonPlaceholder="Why withdraw this proposal? (required)"
            onConfirm={(reason) => withdrawCatchballProposal(submittedProposalId, reason)}
            onSuccess={() => {
              setSubmittedProposalId(null);
              void fetchLoops(cycleId);
            }}
            successTitle="Proposal withdrawn"
            successMessage="It is back with you, unsubmitted."
            className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] underline hover:no-underline"
          />
        </div>
      </section>
    );
  }

  return (
    <section
      id="inbox-pass-up"
      className="border border-slate-blue-200 dark:border-slate-blue-700/60 bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-4 space-y-3 scroll-mt-24"
    >
      <div className="flex items-start gap-2">
        <ArrowUp className="w-4 h-4 mt-0.5 text-slate-blue-600 dark:text-slate-blue-400 shrink-0" />
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {isFinalToAnchor ? CATCHBALL_LABELS.submitProposal : 'Pass results up'}
          </h2>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5">
            {isFinalToAnchor
              ? `Packages your included ranking as a catchball proposal ${targetName ?? 'the next level'} can accept, return, or reject — then moves the ball up Path.`
              : 'Moves Path custody to the stop above. They take the ball on their desk and continue the work.'}
          </p>
        </div>
      </div>

      {stopsAbove.length > 1 && (
        <label className="block space-y-1">
          <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Send to
          </span>
          <select
            value={effectiveTargetId ?? ''}
            onChange={(e) => setTargetOrgUnitId(e.target.value || null)}
            className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
          >
            {stopsAbove.map((id) => (
              <option key={id} value={id}>
                {stopLabel(id)}
                {id === pathAnchorId ? ' (final review)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {stopsAbove.length === 1 && effectiveTargetId && (
        <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          To <span className="font-medium">{targetName}</span>
          {isFinalToAnchor ? ' for final review' : ''}
        </p>
      )}

      <label className="block space-y-1">
        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {isFinalToAnchor ? 'Rationale (required)' : 'Note (optional)'}
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={isFinalToAnchor ? 3 : 2}
          placeholder={
            isFinalToAnchor
              ? `Why this ranking — tradeoffs, disagreements, what ${targetName ?? 'the next level'} should know…`
              : 'Context for the next holder…'
          }
          className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] resize-y"
        />
      </label>

      {includedSessionCount === 0 && (
        <p className="text-sm text-electric-mustard-800 dark:text-electric-mustard-300">
          Mark at least one completed session as included before passing up.{' '}
          <a href="#inbox-send" className="underline font-medium hover:no-underline">
            Choose a session above
          </a>
          {' '}(Include in next send), or Attach to ball if it is not linked yet.
        </p>
      )}
      {isFinalToAnchor && includedSessionCount > 0 && !packagingSessionId && (
        <p className="text-sm text-electric-mustard-800 dark:text-electric-mustard-300">
          No included session has saved ranking results yet.
        </p>
      )}

      <button
        type="button"
        disabled={!canPass || busy}
        onClick={() => void handlePass()}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
        {busy
          ? isFinalToAnchor
            ? 'Submitting…'
            : 'Passing…'
          : isFinalToAnchor
            ? `Submit proposal to ${targetName ?? 'the next level'}`
            : `Pass up to ${targetName ?? 'next stop'}`}
      </button>
    </section>
  );
}
